import type { GoogleClassroomPostType, ShareModeSettings } from "@shared/models/card-stack"
import type { AllAnalyticsEvents } from "../../models/analytics/all"
import { AppUndo } from "../../models/analytics/app"
import {
  LessonCopy,
  LessonEditAttachment,
  LessonEditCard,
  LessonEditGoogleClassroom,
  LessonEditorInteraction,
  LessonEditStandard,
  type LessonAnalyticsCore,
} from "../../models/analytics/lesson"
import type {
  AttachmentId,
  AttachmentSource,
  CardColor,
  CardId,
  EditorBlockKind,
  GoogleClassId,
  GoogleClassroomTextField,
  HeadingLevel,
  ImageId,
  ListKind,
  NonEmptyTuple,
  PickerAttachmentSource,
  StandardId,
} from "./types"

/**
 * Operation = a specific execution of an editing operation by the user.
 * Each operation corresponds to a chain of Tiptap commands.
 *
 * Contract = a class of operations, specified by the operations' name, Args type,
 * and projection onto newAnalyticsEvents.
 *
 * OPERATION_CONTRACTS is a map from operation names to their contracts.
 */

// ----------
// OPERATION_CONTRACTS scaffolding
// ----------

/** What a contract's `analyticsEvents` projection receives: the labeled args plus the analytics core. */
export interface OperationAnalyticsInput<Args> {
  readonly args: Args
  readonly core: LessonAnalyticsCore
}

/**
 * Defines an OPERATION_CONTRACTS registry entry.
 *
 * The `operationName` is added separately, by `stampOperationNames`.
 */
function operation<Args>(declaration: {
  readonly analyticsEvents: (
    input: OperationAnalyticsInput<Args>,
  ) => NonEmptyTuple<AllAnalyticsEvents>
}): Omit<OperationContract<Args>, "operationName"> {
  return declaration
}

/**
 * An entry in the OPERATION_CONTRACTS registry.
 *
 * It defines the contract for operations with the given name: the operation Args,
 * and the projection onto newAnalyticsEvents.
 */
export interface OperationContract<Args> {
  readonly operationName: string
  analyticsEvents(input: OperationAnalyticsInput<Args>): NonEmptyTuple<AllAnalyticsEvents>
}

/**
 * Converts the raw registry into the exported one, by stamping each contract
 * with its registry key.
 */
function stampOperationNames<Declarations extends Record<string, object>>(
  declarations: Declarations,
): {
  readonly [OperationName in keyof Declarations & string]: Declarations[OperationName] & {
    readonly operationName: OperationName
  }
}
function stampOperationNames(declarations: Record<string, object>): Record<string, object> {
  return Object.fromEntries(
    Object.entries(declarations).map(([operationName, declaration]) => [
      operationName,
      { ...declaration, operationName },
    ]),
  )
}

// ----------
// Helpers for defining operations
// ----------

/**
 * Cancels an event if `items` are empty.
 */
function nonEmpty<T>(items: readonly T[]): NonEmptyTuple<T> {
  const [first, ...rest] = items
  if (first === undefined) throw new Error("editor operation produced no analytics events")
  return [first, ...rest]
}

/**
 * The coarse family: operations whose object is the editing surface itself rather than a card, an
 * attachment, or a standard. `LessonEditorInteraction`'s columns are deliberately coarse — the
 * operation, surface, and args ride in `narrative.context` beside the row.
 */
function editorInteraction<Args>(
  verb: LessonEditorInteraction["adverbial_verb"],
): Omit<OperationContract<Args>, "operationName"> {
  return operation<Args>({
    analyticsEvents: ({ core }) => [LessonEditorInteraction.create(core, { verb, cardId: null })],
  })
}

/** The link operations: one attachment edit each, differing only in the verb. */
function attachmentEdit<Args extends { readonly attachmentId: AttachmentId }>(
  verb: LessonEditAttachment["adverbial_verb"],
): Omit<OperationContract<Args>, "operationName"> {
  return operation<Args>({
    analyticsEvents: ({ args, core }) => [
      LessonEditAttachment.create(core, {
        verb,
        specifier: null,
        attachmentId: args.attachmentId,
      }),
    ],
  })
}

function attachmentSpecifier(
  source: AttachmentSource | null,
): LessonEditAttachment["adverbial_object_specifier"] {
  if (source === "upload") return "upload_file"
  if (source === "google") return "google_drive_file"
  if (source === "dropbox") return "dropbox_file"
  if (source === "onedrive") return "one_drive_file"
  if (source === "other" || source === "link") return "other"
  return null
}

function googleClassroomFieldSpecifier(
  field: GoogleClassroomTextField,
): LessonEditGoogleClassroom["adverbial_object_specifier"] {
  return field === "maxPoints" ? "gc_max_points" : field
}

// ----------
// OPERATION_CONTRACTS registry
// ----------

/**
 * The editor operation registry. It maps each operation name to an OperationContract,
 * which defines those operations' Args type and projection onto newAnalyticsEvents.
 */
export const OPERATION_CONTRACTS = stampOperationNames({
  OP_TOGGLE_BOLD: editorInteraction<null>("toggling"),

  OP_TOGGLE_ITALIC: editorInteraction<null>("toggling"),

  OP_TOGGLE_STRIKETHROUGH: editorInteraction<null>("toggling"),

  OP_TOGGLE_UNDERLINE: editorInteraction<null>("toggling"),

  OP_TOGGLE_SUPERSCRIPT: editorInteraction<null>("toggling"),

  OP_TOGGLE_SUBSCRIPT: editorInteraction<null>("toggling"),

  OP_SET_HEADING: editorInteraction<{ readonly level: HeadingLevel | null }>("editing"),

  OP_SET_LIST: editorInteraction<{ readonly list: ListKind }>("editing"),

  OP_SET_TEXT_COLOR: editorInteraction<{ readonly color: string | null }>("editing"),

  OP_SET_HIGHLIGHT: editorInteraction<{ readonly color: string | null }>("editing"),

  OP_SET_UNDERLINE_COLOR: editorInteraction<{
    readonly color: string | null
    readonly clear: boolean
  }>("editing"),

  OP_SET_CARD_COLOR: operation<{ readonly color: CardColor; readonly cardId: CardId }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditCard.create(core, { verb: "editing", specifier: "color", cardId: args.cardId }),
    ],
  }),

  OP_UNDO: operation<null>({
    analyticsEvents: ({ core }) => [AppUndo.create(core, { specifier: "undo" })],
  }),

  OP_REDO: operation<null>({
    analyticsEvents: ({ core }) => [AppUndo.create(core, { specifier: "redo" })],
  }),

  OP_INSERT_LINK: attachmentEdit<{ readonly attachmentId: AttachmentId; readonly url: string }>(
    "adding",
  ),

  /** A typed or pasted URL converting itself into a link token (the autolink Enter handler and
   * input rule). The token is a real attachment, minted by the handler — hence the same fact as
   * an explicit insert, from a different surface. */
  OP_AUTOLINK: attachmentEdit<{ readonly attachmentId: AttachmentId; readonly url: string }>(
    "adding",
  ),

  OP_EDIT_LINK: attachmentEdit<{ readonly attachmentId: AttachmentId; readonly url: string }>(
    "renaming",
  ),

  OP_REMOVE_LINK: attachmentEdit<{ readonly attachmentId: AttachmentId }>("removing"),

  OP_RENAME_LINK: attachmentEdit<{
    readonly attachmentId: AttachmentId
    readonly title: string | null
  }>("renaming"),

  OP_INSERT_FILE: operation<
    | {
        readonly kind: "image"
        readonly fileIds: NonEmptyTuple<ImageId>
        readonly source: PickerAttachmentSource
      }
    | {
        readonly kind: "attachment"
        readonly fileIds: NonEmptyTuple<AttachmentId>
        readonly source: PickerAttachmentSource
      }
  >({
    analyticsEvents: ({ args, core }) =>
      nonEmpty(
        args.fileIds.map((id) =>
          LessonEditAttachment.create(core, {
            verb: "adding",
            specifier: attachmentSpecifier(args.source),
            attachmentId: id,
          }),
        ),
      ),
  }),

  /** Base64 images arriving on the clipboard, each swapped for an uploading placeholder. */
  OP_INSERT_IMAGE: operation<{ readonly imageIds: NonEmptyTuple<ImageId> }>({
    analyticsEvents: ({ args, core }) =>
      nonEmpty(
        args.imageIds.map((id) =>
          LessonEditAttachment.create(core, {
            verb: "adding",
            specifier: "upload_file",
            attachmentId: id,
          }),
        ),
      ),
  }),

  OP_INSERT_BLOCK: operation<{ readonly block: EditorBlockKind; readonly cardId: CardId }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditorInteraction.create(core, { verb: "editing", cardId: args.cardId }),
    ],
  }),

  OP_ADD_CARD: operation<{ readonly cardId: CardId }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditCard.create(core, { verb: "adding", specifier: null, cardId: args.cardId }),
    ],
  }),

  OP_COPY_CARD: operation<{ readonly cardId: CardId }>({
    analyticsEvents: ({ args, core }) => [
      LessonCopy.create(core, { specifier: "cards", objectId: args.cardId }),
    ],
  }),

  OP_PASTE_CARD: operation<{
    readonly sourceCardId: CardId
    readonly destinationCardId: CardId
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditCard.create(core, {
        verb: "pasting",
        specifier: "lesson",
        cardId: args.destinationCardId,
      }),
    ],
  }),

  OP_DELETE_CARD: operation<{ readonly cardId: CardId }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditCard.create(core, { verb: "removing", specifier: null, cardId: args.cardId }),
    ],
  }),

  OP_TOGGLE_CARD_PUBLIC: operation<{ readonly cardId: CardId; readonly isPublic: boolean }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditCard.create(core, { verb: "editing", specifier: "public", cardId: args.cardId }),
    ],
  }),

  OP_POST_CARD_TO_GOOGLE_CLASSROOM: operation<{
    readonly cardId: CardId
    readonly postType: GoogleClassroomPostType
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditCard.create(core, { verb: "adding", specifier: null, cardId: args.cardId }),
    ],
  }),

  OP_MERGE_CARD_BACKWARD: operation<{
    readonly sourceCardId: CardId
    readonly destinationCardId: CardId
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditCard.create(core, {
        verb: "editing",
        specifier: null,
        cardId: args.destinationCardId,
      }),
    ],
  }),

  // `newCardId: null` is a split whose moved text landed in the card already below the title.
  OP_SPLIT_LESSON_TITLE: operation<{ readonly newCardId: CardId | null }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditCard.create(core, { verb: "adding", specifier: null, cardId: args.newCardId }),
    ],
  }),

  OP_EDIT_STANDARDS: operation<{
    readonly cardId: CardId | null
    readonly addedIds: readonly StandardId[]
    readonly removedIds: readonly StandardId[]
  }>({
    analyticsEvents: ({ args, core }) =>
      nonEmpty([
        ...args.addedIds.map((standardId) =>
          LessonEditStandard.create(core, { verb: "adding", standardId }),
        ),
        ...args.removedIds.map((standardId) =>
          LessonEditStandard.create(core, { verb: "removing", standardId }),
        ),
      ]),
  }),

  OP_COPY_STANDARD: operation<{ readonly cardId: CardId; readonly standardId: StandardId }>({
    analyticsEvents: ({ args, core }) => [
      LessonCopy.create(core, { specifier: "standards", objectId: args.standardId }),
    ],
  }),

  OP_DELETE_STANDARD: operation<{ readonly cardId: CardId; readonly standardId: StandardId }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditStandard.create(core, { verb: "removing", standardId: args.standardId }),
    ],
  }),

  OP_TOGGLE_STANDARD_EMPHASIS: operation<{
    readonly cardId: CardId
    readonly standardId: StandardId
    readonly emphasized: boolean
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditStandard.create(core, { verb: "starring", standardId: args.standardId }),
    ],
  }),

  /** A standard widget's expand/collapse toggle: a document mutation, but a view preference —
   * so it reports as an editor interaction rather than an edit to the standard. */
  OP_EXPAND_STANDARD: editorInteraction<{ readonly expanded: boolean }>("toggling"),

  OP_EDIT_GOOGLE_CLASSROOM_FIELD: operation<{
    readonly cardId: CardId
    readonly field: GoogleClassroomTextField
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditGoogleClassroom.create(core, {
        specifier: googleClassroomFieldSpecifier(args.field),
        cardId: args.cardId,
      }),
    ],
  }),

  OP_SET_GOOGLE_CLASSROOM_TOPIC: operation<{
    readonly cardId: CardId
    readonly topic: string | null
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditGoogleClassroom.create(core, { specifier: "gc_topic", cardId: args.cardId }),
    ],
  }),

  OP_SET_GOOGLE_CLASSROOM_CHOICES: operation<{
    readonly cardId: CardId
    readonly choices: readonly string[]
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditGoogleClassroom.create(core, {
        specifier: "gc_mc_questions",
        cardId: args.cardId,
      }),
    ],
  }),

  OP_INSERT_GOOGLE_CLASSROOM_ATTACHMENT: operation<{
    readonly cardId: CardId
    readonly attachmentIds: NonEmptyTuple<AttachmentId>
    readonly source: AttachmentSource
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditGoogleClassroom.create(core, { specifier: "value", cardId: args.cardId }),
      ...args.attachmentIds.map((attachmentId) =>
        LessonEditAttachment.create(core, {
          verb: "adding",
          specifier: attachmentSpecifier(args.source),
          attachmentId,
        }),
      ),
    ],
  }),

  OP_REMOVE_GOOGLE_CLASSROOM_ATTACHMENT: operation<{
    readonly cardId: CardId
    readonly attachmentId: AttachmentId
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditAttachment.create(core, {
        verb: "removing",
        specifier: null,
        attachmentId: args.attachmentId,
      }),
      LessonEditGoogleClassroom.create(core, { specifier: "value", cardId: args.cardId }),
    ],
  }),

  OP_SET_GOOGLE_CLASSROOM_SHARE_MODE: operation<{
    readonly cardId: CardId
    readonly attachmentId: AttachmentId
    readonly shareMode: ShareModeSettings
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditAttachment.create(core, {
        verb: "renaming",
        specifier: null,
        attachmentId: args.attachmentId,
      }),
      LessonEditGoogleClassroom.create(core, {
        specifier: "gc_share_mode",
        cardId: args.cardId,
      }),
    ],
  }),

  OP_SCHEDULE_GOOGLE_CLASSROOM_CLASS: operation<{
    readonly cardId: CardId
    readonly googleClassId: GoogleClassId
    readonly scheduled: boolean
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditGoogleClassroom.create(core, {
        specifier: args.scheduled ? "gc_schedule" : "gc_unschedule",
        cardId: args.cardId,
      }),
    ],
  }),

  OP_SET_GOOGLE_CLASSROOM_CLASS_TIME: operation<{
    readonly cardId: CardId
    readonly googleClassId: GoogleClassId
    readonly field: "scheduledAtTime" | "dueAt"
  }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditGoogleClassroom.create(core, { specifier: "gc_time", cardId: args.cardId }),
    ],
  }),

  /** Clipboard content arriving through a handler we own. `cardId` is the card the caret sat in
   * when the paste was handled, or null where the handler has no view to ask. */
  OP_PASTE: operation<{ readonly cardId: CardId | null }>({
    analyticsEvents: ({ args, core }) => [
      LessonEditorInteraction.create(core, { verb: "pasting", cardId: args.cardId }),
    ],
  }),

  OP_DRAG: editorInteraction<{ readonly moved: boolean; readonly crossEditor: boolean }>(
    "dragging_dropping",
  ),
})

/**
 * Type of OPERATION_CONTRACTS operation names.
 */
export type OperationName = keyof typeof OPERATION_CONTRACTS & string

/** Extracts Args from an operation in OPERATION_CONTRACTS. */
export type OperationArgs<Contract> = Contract extends OperationContract<infer Args> ? Args : never
